import { RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router';
import { LongArrow } from '@/components/brand/long-arrow';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { STAGE_PRIMARY_BUTTON } from '@/components/whats-new/editions/estreia/cover-slide';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import {
  outroLeadLongest,
  outroVM,
} from '@/components/whats-new/editions/estreia/view-models';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { Rise } from '@/components/whats-new/slides/rise';
import {
  SLIDE_LEAD,
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { StoreChip } from '@/components/whats-new/slides/store-chip';
import { TextReserve } from '@/components/whats-new/slides/text-reserve';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

/**
 * 9 · Encerramento (NEWS-12, NEWS-36): "Por hoje é isso", the store in the
 * title, where to find the top campaign's pin and the next one, then
 * "Abrir a nova Início" (closes and goes to the Início) and "Ver de novo"
 * (back to the first slide). No reactions.
 */
export function OutroSlide({ goTo, close }: SlideProps) {
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { store, lead } = outroVM(useEstreiaFacts());

  const openHome = () => {
    close();
    navigate(`/${tenant.slug}/dashboard`);
  };

  return (
    <SlideLayout
      variant="cover"
      copy={
        <>
          <Rise index={0}>
            <Eyebrow
              lead={LL.whatsNew.editions.estreia.outro.eyebrowLead()}
              trail={LL.whatsNew.editions.estreia.outro.eyebrowTrail()}
            />
          </Rise>
          <SlideTitle>
            <WordReveal
              text={LL.whatsNew.editions.estreia.outro.title({
                chip: '{chip}',
              })}
              slots={{
                chip: <StoreChip name={store.name} iconUrl={store.iconUrl} />,
              }}
            />
          </SlideTitle>
          <Rise index={2}>
            {/* ≥ 768px the title and the buttons never move: the lead holds
                its longest room (FX8c). Not below, so the CTA stays above the
                fold on phones (FX8d, user decision). */}
            <TextReserve
              longest={outroLeadLongest(LL)}
              className={SLIDE_LEAD}
              fromMd
            >
              {lead.state === 'ready' ? (
                <SlideLead>{lead.value(LL)}</SlideLead>
              ) : (
                <div
                  data-testid="outro-lead-loading"
                  className="flex flex-col gap-2"
                >
                  <Skeleton className="h-4 w-full max-w-[400px] bg-white/[.12]" />
                  <Skeleton className="h-4 w-2/3 max-w-[280px] bg-white/[.12]" />
                </div>
              )}
            </TextReserve>
          </Rise>
          <Rise index={3} className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={openHome}
              className={STAGE_PRIMARY_BUTTON}
            >
              {LL.whatsNew.editions.estreia.outro.cta()}
              <LongArrow lineClassName="w-7 group-hover:w-11" />
            </button>
            <button
              type="button"
              onClick={() => goTo(0)}
              className="relative z-[5] inline-flex h-[42px] items-center gap-2 px-1 text-[13.5px] font-semibold text-white/[.86] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6FA0FF] max-md:min-h-touch"
            >
              <RotateCcw
                aria-hidden="true"
                className="size-4"
                strokeWidth={1.6}
              />
              {LL.whatsNew.editions.estreia.outro.restart()}
            </button>
          </Rise>
        </>
      }
    />
  );
}
